import { forwardRef, useState } from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import Collapse from "@mui/material/Collapse";
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import type {
    Filter as FilterType,
    AbstractFilterProps,
    FiltersProps
} from 'ui/widgets/Filters';
import Button from 'ui/atoms/Button';
import ToggleButton from 'ui/atoms/ToggleButton';
import {
    Filters,
    Summables,
    Widgetable,
    SelectedFilters
} from 'ui/widgets/Filters';
import useTableFilters, { type TableFiltersProps } from '../hooks/useTableFilters';
import type { FiltersPublicApi } from '../types';
import { TableGridMode } from 'consts/table';

type Props =
    & TableFiltersProps<string>
    & Pick<
        FiltersProps,
        | 'FrontPanelWidget'
        | 'SummableWidget'
        | 'sx'
    >;

const TableFilters = forwardRef<FiltersPublicApi<string> | null, Props>(({ sx, ...props }, ref) => {
    const {
        SummableWidget,
        FrontPanelWidget
    } = props;

    const {
        filters,
        onChange,
        onReset,
        onApply,
        isFiltersOpen,
        handleToggleFilters,
        getWidgetProps,
        isFiltersAvailable,
        filterPropsOverride,
        filterVisibilityPredicate,
        getFilterOptions,
        isBootstrapDataLoading
    } = useTableFilters({
        ...props,
        ref
    });

    const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({
        'Finance / Frequently used': true
    });
    const handleGroupChange = (groupName: string) =>
        (_: unknown, isExpanded: boolean) =>
            setExpandedGroups(state => ({
                ...state,
                [groupName]: isExpanded
            }));
    // const [isFiltersOpen, setFiltersOpen] = useState<boolean>(false);

    // const isBootstrapDataLoading = useTypedSelector(selectIsBootstrapDataLoading);
    // const { getFilterOptions } = useFiltersModel(filtersModelOverrides);

    // const filterPropsOverride = useFilterPropsOverride!(filters);
    // const filterVisibilityPredicate = useVisibilityRules!(filters);

    // const handleToggleFilters = () => setFiltersOpen(state => !state);

    // const getWidgetProps = (Component?: WidgetProps['Component']) => ({
    //     Component,
    //     filters,
    //     isFiltersOpen
    // });

    // const isFiltersAvailable = () => filters.length > 0;

    if (!isFiltersAvailable()) {
        return null;
    }

    const isOpenBankingMode = [
        TableGridMode.OpenBankingBankingSDK,
        TableGridMode.OpenBankingIbanXS,
        TableGridMode.OpenBankingBankingSDKRecipients,
        TableGridMode.OpenBankingBankingSDKConnectorsRecipientsPivot,
        TableGridMode.OpenBankingIbanXSBanksRecipientsPivot,
        TableGridMode.OpenBankingIbanXSBanksCountriesPivot
    ].includes(props.mode);

    const dataTestIdPrefix = isOpenBankingMode
        ? 'openBanking.table.filters'
        : 'table.tableFilters';

    return (
        <Box
            sx={{
                m: 3,
                display: 'grid',
                gridTemplateColumns: `1fr ${SummableWidget ? 'auto' : ''}`,
                gridTemplateRows: 'repeat(2, auto)',
                ...sx
            }}
        >
            <Card>
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: '1fr auto'
                    }}
                >
                    <Stack
                        m={2}
                        direction="row"
                        spacing={2}
                    >
                        <ToggleButton
                            data-test-id={`${dataTestIdPrefix}.toggleOpen`}
                            isActive={isFiltersOpen}
                            onClick={handleToggleFilters}
                            variant="outlined"
                            startIcon={<FilterAltOutlinedIcon />}
                            endIcon={<ArrowDropDownIcon />}
                        >
                            {isFiltersOpen ? 'Hide Filter' : 'Show Filter'}
                        </ToggleButton>
                        <Button
                            data-test-id={`${dataTestIdPrefix}.apply`}
                            variant="outlined"
                            startIcon={<ArrowUpwardIcon />}
                            size='small'
                            onClick={onApply}
                        >
                            Apply
                        </Button>
                        <Button
                            data-test-id={`${dataTestIdPrefix}.reset`}
                            variant="outlined"
                            startIcon={<RestartAltIcon />}
                            size='small'
                            onClick={onReset}
                        >
                            Reset
                        </Button>
                    </Stack>
                    <Box
                        m={2}
                    >
                        <Widgetable
                            {...getWidgetProps(FrontPanelWidget)}
                        />
                    </Box>
                </Box>
                <Collapse in={isFiltersOpen} timeout="auto" unmountOnExit>
                    <Box m={2}>
                        {(() => {
                            const groupedFilters = filters.reduce((acc, filter) => {
                                if (!filter.group) {
                                    return acc;
                                }
                                if (!acc[filter.group]) {
                                    acc[filter.group] = [];
                                }
                                acc[filter.group].push(filter);
                                return acc;
                            }, {} as Record<string, typeof filters>);

                            const groupOrder = [
                                'Finance / Frequently used',
                                'Settlements / Funds related',
                                'Other transaction related',
                                'Customer / personal details'
                            ];

                            return Object.entries(groupedFilters)
                                .sort(([groupA], [groupB]) => {
                                    const indexA = groupOrder.indexOf(groupA);
                                    const indexB = groupOrder.indexOf(groupB);
                                    return (indexA === -1 ? Infinity : indexA) - (indexB === -1 ? Infinity : indexB);
                                })
                                .map(([groupName, groupFilters]) => (
                                    <Accordion
                                        key={groupName}
                                        expanded={Boolean(expandedGroups[groupName])}
                                        onChange={handleGroupChange(groupName)}
                                        disableGutters
                                        square
                                        elevation={0}
                                        sx={{
                                            border: '1px solid',
                                            borderColor: 'divider',
                                            borderRadius: 1,
                                            mb: 1,
                                            '&:before': { display: 'none' }
                                        }}
                                    >
                                        <AccordionSummary
                                            expandIcon={<ExpandMoreIcon />}
                                            sx={{
                                                '& .MuiAccordionSummary-content': {
                                                    fontSize: '0.875rem',
                                                    fontWeight: 600,
                                                    color: '#000'
                                                }
                                            }}
                                        >
                                            {groupName}
                                        </AccordionSummary>
                                        <AccordionDetails>
                                            <Stack direction="row" flexWrap='wrap' gap={2}>
                                                {groupFilters.map(filter => {
                                                    const filterProps = {
                                                        renderAs: filter.renderAs,
                                                        props: {
                                                            ...getFilterOptions(filter.props.id),
                                                            ...filter.props,
                                                            ...filterPropsOverride(filter)
                                                        }
                                                    };

                                                    if (!filterVisibilityPredicate(filter)) {
                                                        return null;
                                                    }

                                                    return (
                                                        <Box
                                                            key={filter.props.id}
                                                            data-test-id={`${dataTestIdPrefix}.item.${filter.props.id}`}
                                                        >
                                                            <Filters
                                                                {...filterProps}
                                                                onChange={onChange}
                                                            />
                                                        </Box>
                                                    );
                                                })}
                                            </Stack>
                                        </AccordionDetails>
                                    </Accordion>
                                ));
                        })()}
                    </Box>
                </Collapse>
            </Card>
            <Summables
                {...getWidgetProps(SummableWidget)}
            />
            <SelectedFilters
                isOpen={isFiltersOpen}
                isBootstrapDataLoading={isBootstrapDataLoading}
                filters={filters}
                getFilterOptions={getFilterOptions}
                onChange={onChange}
            />
        </Box>
    );
});

TableFilters.defaultProps = {
    useVisibilityRules: () => _ => true,
    useFilterPropsOverride: () => _ => ({}) as FilterType<AbstractFilterProps>
};

export default TableFilters;
